import { useState, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import type { BacklogTask, Tarea, Proyecto, TareaMensaje } from '@/lib/types'
import { formatDate } from '@/lib/utils'
import {
  IconPlus, IconSearch, IconUser, IconFileText, IconClock,
  IconArrowRight, IconTrash,
  IconExternalLink, IconFilter, IconEdit, IconBot, IconZap,
  IconMessageCircle, IconLightbulb, IconPaperclip, IconPhone, IconMail,
} from '@/components/icons/Icons'
import { showToast } from '@/components/layout/Toast'
import AsignarProyecto from '@/components/backlog/AsignarProyecto'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/ui'

const PRIORIDAD_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  alta: { label: 'Alta', color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
  media: { label: 'Media', color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
  baja: { label: 'Baja', color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
}

const ORIGEN_BADGE: Record<string, { label: string; icon: 'edit' | 'bot' | 'zap' }> = {
  manual: { label: 'Manual', icon: 'edit' },
  ai_chat: { label: 'IA Chat', icon: 'bot' },
  ai_cron: { label: 'IA Auto', icon: 'zap' },
}

const ORIGEN_ICON_MAP = {
  edit: IconEdit,
  bot: IconBot,
  zap: IconZap,
} as const

const ESTADO_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  pendiente: { label: 'Pendiente', color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 12%, transparent)' },
  en_progreso: { label: 'En Progreso', color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
  completada: { label: 'Completada', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' },
  bloqueada: { label: 'Bloqueada', color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
}

export default function Backlog() {
  const api = useApi()
  const [search, setSearch] = useState('')
  const [filterPrioridad, setFilterPrioridad] = useState<string[]>([])
  const [filterOrigen, setFilterOrigen] = useState<string[]>([])
  const [filterEstado, setFilterEstado] = useState<string[]>([])
  const [filterContacto, setFilterContacto] = useState('')
  const [showFilterModal, setShowFilterModal] = useState(false)

  // Temp filter states for the modal (applied on "Apply")
  const [tmpPrioridad, setTmpPrioridad] = useState<string[]>([])
  const [tmpOrigen, setTmpOrigen] = useState<string[]>([])
  const [tmpEstado, setTmpEstado] = useState<string[]>([])
  const [tmpContacto, setTmpContacto] = useState('')
  const [selectedTask, setSelectedTask] = useState<BacklogTask | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [promoteTaskId, setPromoteTaskId] = useState<number | null>(null)
  const [promoteProjectId, setPromoteProjectId] = useState<number>(0)

  const { data: tasks = [], isLoading: loading, refrescar: loadData, actualizar } = useCachedData(
    ['backlog'],
    () => api.get<BacklogTask[]>('/api/backlog').catch(err => {
      console.error('Error loading backlog:', err)
      return [] as BacklogTask[]
    }),
  )
  // Clave `proyectos`: la misma que usan Proyectos y el selector, asi que entrar
  // aca despues del Kanban no la vuelve a pedir.
  const { data: projects = [] } = useCachedQuery<Proyecto>('proyectos')

  // Los activos, para cuando ninguno de los sugeridos sirva. Se derivan de la
  // lista de arriba: antes esto era un `fetch` suelto al MISMO endpoint, o sea
  // la misma consulta dos veces en cada visita.
  const proyectosActivos = useMemo(
    () => projects
      .filter(p => p.estado === 'activo')
      .map(p => ({ id: p.id, nombre: p.nombre, descripcion: p.descripcion })),
    [projects],
  )

  async function loadTaskDetail(taskId: number) {
    setDetailLoading(true)
    try {
      const detail = await api.get<BacklogTask>(`/api/backlog/${taskId}`)
      setSelectedTask(detail)
    } catch (err) {
      console.error('Error loading task detail:', err)
    } finally {
      setDetailLoading(false)
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('¿Eliminar esta tarea del backlog?')) return
    try {
      await api.del(`/api/backlog/${id}`)
      showToast('Tarea eliminada', 'success')
      if (selectedTask?.id === id) setSelectedTask(null)
      loadData()
    } catch { showToast('Error al eliminar', 'error') }
  }

  async function handlePromote(taskId: number) {
    if (!promoteProjectId) return
    try {
      await api.post(`/api/backlog/${taskId}/promote`, { proyecto_id: promoteProjectId })
      showToast('Tarea promovida al proyecto', 'success')
      setPromoteTaskId(null)
      setPromoteProjectId(0)
      if (selectedTask?.id === taskId) setSelectedTask(null)
      loadData()
    } catch { showToast('Error al promover', 'error') }
  }

  // Active filter count for badge
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filterPrioridad.length > 0) count++
    if (filterOrigen.length > 0) count++
    if (filterEstado.length > 0) count++
    if (filterContacto.trim()) count++
    return count
  }, [filterPrioridad, filterOrigen, filterEstado, filterContacto])

  function openFilterModal() {
    setTmpPrioridad([...filterPrioridad])
    setTmpOrigen([...filterOrigen])
    setTmpEstado([...filterEstado])
    setTmpContacto(filterContacto)
    setShowFilterModal(true)
  }

  function applyFilters() {
    setFilterPrioridad([...tmpPrioridad])
    setFilterOrigen([...tmpOrigen])
    setFilterEstado([...tmpEstado])
    setFilterContacto(tmpContacto)
    setShowFilterModal(false)
  }

  function clearFilters() {
    setTmpPrioridad([])
    setTmpOrigen([])
    setTmpEstado([])
    setTmpContacto('')
  }

  function toggleFilter(arr: string[], val: string, setter: (v: string[]) => void) {
    setter(arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val])
  }

  // Filtered tasks
  const filtered = useMemo(() => {
    return tasks
      .filter(t => !t.parent_id) // Only show top-level tasks
      .filter(t => {
        if (filterPrioridad.length > 0 && !filterPrioridad.includes(t.prioridad)) return false
        if (filterOrigen.length > 0 && !filterOrigen.includes(t.origen)) return false
        if (filterEstado.length > 0 && !filterEstado.includes(t.estado)) return false
        if (filterContacto.trim()) {
          const q = filterContacto.toLowerCase()
          const name = `${t.contacto_nombre || ''} ${t.contacto_apellido || ''}`.toLowerCase()
          const phone = (t.contacto_telefono || '').toLowerCase()
          const email = (t.contacto_email || '').toLowerCase()
          if (!name.includes(q) && !phone.includes(q) && !email.includes(q)) return false
        }
        if (search) {
          const q = search.toLowerCase()
          const name = `${t.contacto_nombre || ''} ${t.contacto_apellido || ''}`.toLowerCase()
          return t.titulo.toLowerCase().includes(q) || name.includes(q) ||
            (t.descripcion || '').toLowerCase().includes(q)
        }
        return true
      })
  }, [tasks, search, filterPrioridad, filterOrigen, filterEstado, filterContacto])

  // Count subtasks per parent
  const subtaskMap = useMemo(() => {
    const map = new Map<number, Tarea[]>()
    tasks.filter(t => t.parent_id).forEach(t => {
      const arr = map.get(t.parent_id!) || []
      arr.push(t)
      map.set(t.parent_id!, arr)
    })
    return map
  }, [tasks])

  if (loading) {
    return (
      <div className="page-enter" id="page-backlog" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <div className="auth-spinner" style={{ width: 32, height: 32 }} />
      </div>
    )
  }

  return (
    <div className="page-enter" id="page-backlog">
      <PageHeader
        breadcrumbs={[{ label: 'Operaciones' }, { label: 'Backlog' }]}
        meta={`${filtered.length} sin proyecto`}
        actions={
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <IconPlus size={16} /> Nueva Tarea
          </button>
        }
      />

      {/* Filters */}
      <div className="backlog-filters">
        <div className="search-bar" style={{ flex: 1, maxWidth: 320 }}>
          <IconSearch size={16} />
          <input
            type="text"
            placeholder="Buscar por título, contacto..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button
          className="btn btn-secondary"
          onClick={openFilterModal}
          style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          title="Filtros"
        >
          <IconFilter size={16} />
          Filtros
          {activeFilterCount > 0 && (
            <span style={{
              position: 'absolute', top: -6, right: -6,
              background: 'var(--accent-primary)', color: 'var(--text-on-primary)',
              borderRadius: '50%', width: 18, height: 18,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.65rem', fontWeight: 700,
            }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Main content: list + detail panel */}
      <div className="backlog-layout">
        {/* Task list */}
        <div className="backlog-list">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <line x1="9" y1="10" x2="15" y2="10"/>
              </svg>
              <h3>Sin tareas en el backlog</h3>
              <p>Las tareas aparecerán aquí cuando se detecten desde el chat o se creen manualmente</p>
            </div>
          ) : (
            filtered.map(task => {
              const prio = PRIORIDAD_BADGE[task.prioridad] || PRIORIDAD_BADGE.media
              const origen = ORIGEN_BADGE[task.origen] || ORIGEN_BADGE.manual
              const estado = ESTADO_BADGE[task.estado] || ESTADO_BADGE.pendiente
              const subtasks = subtaskMap.get(task.id) || []
              const isSelected = selectedTask?.id === task.id

              return (
                <div key={task.id}>
                  <div
                    className={`backlog-card ${isSelected ? 'active' : ''}`}
                    onClick={() => loadTaskDetail(task.id)}
                  >
                    <div className="backlog-card-header">
                      <span className="badge" style={{ background: prio.bg, color: prio.color, fontSize: '0.7rem' }}>
                        {prio.label}
                      </span>
                      <span className="badge" style={{ background: estado.bg, color: estado.color, fontSize: '0.7rem' }}>
                        {estado.label}
                      </span>
                      <span className="backlog-origen-badge" title={`Origen: ${origen.label}`}>
                        {(() => { const OrigenIcon = ORIGEN_ICON_MAP[origen.icon]; return <OrigenIcon size={12} /> })()}
                      </span>
                      {task.ai_confidence != null && (
                        <span className="backlog-confidence" title={`Confianza IA: ${Math.round(task.ai_confidence * 100)}%`}>
                          {Math.round(task.ai_confidence * 100)}%
                        </span>
                      )}
                    </div>
                    <h4 className="backlog-card-title">{task.titulo}</h4>
                    {task.descripcion && (
                      <p className="backlog-card-desc">{task.descripcion.substring(0, 120)}{task.descripcion.length > 120 ? '...' : ''}</p>
                    )}
                    <div className="backlog-card-meta">
                      {(task.contacto_nombre || task.contacto_apellido) && (
                        <span className="backlog-meta-item">
                          <IconUser size={12} />
                          {task.contacto_nombre} {task.contacto_apellido || ''}
                        </span>
                      )}
                      <span className="backlog-meta-item">
                        <IconClock size={12} />
                        {formatDate(task.created_at?.split('T')[0] || '')}
                      </span>
                      {subtasks.length > 0 && (
                        <span className="backlog-meta-item backlog-subtask-count">
                          +{subtasks.length} sub-tarea{subtasks.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    {/* Promote dropdown */}
                    {promoteTaskId === task.id && (
                      <div className="backlog-promote-dropdown" onClick={e => e.stopPropagation()}>
                        <select
                          className="form-select"
                          value={promoteProjectId}
                          onChange={e => setPromoteProjectId(Number(e.target.value))}
                          style={{ fontSize: '0.82rem' }}
                        >
                          <option value={0}>— Seleccionar proyecto —</option>
                          {projects.map(p => (
                            <option key={p.id} value={p.id}>{p.nombre}</option>
                          ))}
                        </select>
                        <button
                          className="btn btn-sm btn-primary"
                          disabled={!promoteProjectId}
                          onClick={() => handlePromote(task.id)}
                        >
                          Mover
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => { setPromoteTaskId(null); setPromoteProjectId(0) }}
                        >
                          ✕
                        </button>
                      </div>
                    )}

                    {/* Card actions at bottom */}
                    <div className="backlog-card-actions" onClick={e => e.stopPropagation()}>
                      <button className="btn btn-icon btn-sm btn-secondary" onClick={() => setPromoteTaskId(task.id)} title="Promover a proyecto">
                        <IconArrowRight size={13} />
                      </button>
                      <button className="btn btn-icon btn-sm btn-secondary" onClick={() => handleDelete(task.id)} title="Eliminar" style={{ color: 'var(--danger)' }}>
                        <IconTrash size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Subtasks (if any) */}
                  {subtasks.length > 0 && isSelected && (
                    <div className="backlog-subtasks">
                      {subtasks.map(st => (
                        <div key={st.id} className="backlog-subtask-item">
                          <span className="backlog-subtask-dot" />
                          <span className="backlog-subtask-title">{st.titulo}</span>
                          <span className="backlog-meta-item" style={{ marginLeft: 'auto' }}>
                            {formatDate(st.created_at?.split('T')[0] || '')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Detail panel */}
        <div className="backlog-detail">
          {!selectedTask ? (
            <div className="backlog-detail-empty">
              <IconFileText size={40} style={{ opacity: 0.3 }} />
              <p>Selecciona una tarea para ver los detalles</p>
            </div>
          ) : detailLoading ? (
            <div className="backlog-detail-empty">
              <div className="auth-spinner" style={{ width: 24, height: 24 }} />
            </div>
          ) : (
            <div className="backlog-detail-content">
              {/* Task header */}
              <div className="backlog-detail-header">
                <h2>{selectedTask.titulo}</h2>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {selectedTask.prioridad && (() => {
                    const p = PRIORIDAD_BADGE[selectedTask.prioridad]
                    return <span className="badge" style={{ background: p?.bg, color: p?.color }}>{p?.label}</span>
                  })()}
                  {selectedTask.origen && (() => {
                    const o = ORIGEN_BADGE[selectedTask.origen]
                    const OrigenIcon = o ? ORIGEN_ICON_MAP[o.icon] : null
                    return <span className="badge" style={{ background: 'color-mix(in srgb, var(--primary-light) 12%, transparent)', color: 'var(--accent-primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>{OrigenIcon && <OrigenIcon size={12} />} {o?.label}</span>
                  })()}
                </div>
              </div>

              <AsignarProyecto
                tareaId={selectedTask.id}
                todos={proyectosActivos}
                onAsignada={() => {
                  // Sale del backlog en cuanto tiene proyecto: esta lista es justamente
                  // «lo que todavía no tiene dónde ir».
                  actualizar(ts => ts.filter(t => t.id !== selectedTask.id))
                  setSelectedTask(null)
                }}
              />

              {/* Description */}
              {selectedTask.descripcion && (
                <div className="backlog-detail-section">
                  <h4>Descripción</h4>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{selectedTask.descripcion}</p>
                </div>
              )}

              {/* Follow-up sugerido */}
              {selectedTask.followup_sugerido && (
                <div className="backlog-detail-section backlog-followup">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconLightbulb size={14} /> Follow-up Sugerido</h4>
                  <p>{selectedTask.followup_sugerido}</p>
                </div>
              )}

              {/* Contact info */}
              {selectedTask.contact && (
                <div className="backlog-detail-section">
                  <h4><IconUser size={14} /> Contacto</h4>
                  <div className="backlog-contact-card">
                    <strong>{selectedTask.contact.nombre} {selectedTask.contact.apellido || ''}</strong>
                    {selectedTask.contact.telefono && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconPhone size={12} /> {selectedTask.contact.telefono}</span>}
                    {selectedTask.contact.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconMail size={12} /> {selectedTask.contact.email}</span>}
                  </div>
                </div>
              )}

              {/* Referenced messages */}
              {selectedTask.mensajes && selectedTask.mensajes.length > 0 && (
                <div className="backlog-detail-section">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconMessageCircle size={14} /> Mensajes Referenciados ({selectedTask.mensajes.length})</h4>
                  <div className="backlog-messages">
                    {selectedTask.mensajes.map((msg: TareaMensaje) => (
                      <div key={msg.id} className={`backlog-msg ${msg.direccion === 'entrante' ? 'incoming' : 'outgoing'}`}>
                        <div className="backlog-msg-header">
                          <span className="backlog-msg-sender">{msg.remitente || (msg.direccion === 'entrante' ? 'Contacto' : '1Conecta')}</span>
                          <span className="backlog-msg-time">{msg.fecha_mensaje ? new Date(msg.fecha_mensaje).toLocaleString('es-PE') : ''}</span>
                        </div>
                        {msg.contenido && <p className="backlog-msg-text">{msg.contenido}</p>}
                        {msg.media_url && msg.tipo === 'imagen' && (
                          <img src={msg.media_url} alt={msg.media_nombre || ''} className="backlog-msg-img" />
                        )}
                        {msg.media_url && msg.tipo === 'audio' && (
                          <audio controls src={msg.media_url} className="backlog-msg-audio" />
                        )}
                        {msg.media_url && msg.tipo === 'documento' && (
                          <a href={msg.media_url} target="_blank" rel="noopener noreferrer" className="backlog-msg-doc">
                            <IconFileText size={14} /> {msg.media_nombre || 'Documento'}
                          </a>
                        )}
                        {msg.extracto && (
                          <div className="backlog-msg-extracto">
                            <em>Extracto: {msg.extracto}</em>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sub-tasks */}
              {selectedTask.subtasks && selectedTask.subtasks.length > 0 && (
                <div className="backlog-detail-section">
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: 6 }}><IconPaperclip size={14} /> Sub-tareas ({selectedTask.subtasks.length})</h4>
                  {selectedTask.subtasks.map((st: Tarea) => (
                    <div key={st.id} className="backlog-subtask-detail">
                      <strong>{st.titulo}</strong>
                      {st.descripcion && <p>{st.descripcion}</p>}
                      {st.followup_sugerido && <p className="backlog-followup-inline" style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}><IconLightbulb size={12} style={{ flexShrink: 0, marginTop: 3 }} /> {st.followup_sugerido}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Link to conversation */}
              {selectedTask.conversacion_id && (
                <div className="backlog-detail-section">
                  <Link
                    to={`/conversaciones`}
                    className="btn btn-secondary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <IconExternalLink size={14} /> Ver conversación
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create modal (simplified) */}
      {showCreateModal && (
        <BacklogCreateModal
          onClose={() => setShowCreateModal(false)}
          onSaved={() => { setShowCreateModal(false); loadData() }}
        />
      )}

      {/* Filter modal */}
      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>Filtros</h3>
              <button className="btn btn-icon btn-secondary" onClick={() => setShowFilterModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Priority checkboxes */}
              <div>
                <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Prioridad</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {Object.entries(PRIORIDAD_BADGE).map(([key, val]) => (
                    <label key={key} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                      padding: '6px 12px', borderRadius: 8,
                      background: tmpPrioridad.includes(key) ? val.bg : 'transparent',
                      border: `1px solid ${tmpPrioridad.includes(key) ? val.color : 'var(--border)'}`,
                      color: tmpPrioridad.includes(key) ? val.color : 'var(--text-secondary)',
                      transition: 'all 0.15s ease',
                    }}>
                      <input
                        type="checkbox"
                        checked={tmpPrioridad.includes(key)}
                        onChange={() => toggleFilter(tmpPrioridad, key, setTmpPrioridad)}
                        style={{ display: 'none' }}
                      />
                      {val.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Origin checkboxes */}
              <div>
                <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Origen</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {Object.entries(ORIGEN_BADGE).map(([key, val]) => (
                    <label key={key} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                      padding: '6px 12px', borderRadius: 8,
                      background: tmpOrigen.includes(key) ? 'color-mix(in srgb, var(--primary-light) 12%, transparent)' : 'transparent',
                      border: `1px solid ${tmpOrigen.includes(key) ? 'var(--accent-primary)' : 'var(--border)'}`,
                      color: tmpOrigen.includes(key) ? 'var(--accent-primary)' : 'var(--text-secondary)',
                      transition: 'all 0.15s ease',
                    }}>
                      <input
                        type="checkbox"
                        checked={tmpOrigen.includes(key)}
                        onChange={() => toggleFilter(tmpOrigen, key, setTmpOrigen)}
                        style={{ display: 'none' }}
                      />
                      {(() => { const OIcon = ORIGEN_ICON_MAP[val.icon]; return <OIcon size={12} /> })()} {val.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Status checkboxes */}
              <div>
                <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Estado</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {Object.entries(ESTADO_BADGE).map(([key, val]) => (
                    <label key={key} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                      padding: '6px 12px', borderRadius: 8,
                      background: tmpEstado.includes(key) ? val.bg : 'transparent',
                      border: `1px solid ${tmpEstado.includes(key) ? val.color : 'var(--border)'}`,
                      color: tmpEstado.includes(key) ? val.color : 'var(--text-secondary)',
                      transition: 'all 0.15s ease',
                    }}>
                      <input
                        type="checkbox"
                        checked={tmpEstado.includes(key)}
                        onChange={() => toggleFilter(tmpEstado, key, setTmpEstado)}
                        style={{ display: 'none' }}
                      />
                      {val.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Contact search */}
              <div>
                <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Contacto</label>
                <div className="search-bar" style={{ width: '100%' }}>
                  <IconSearch size={14} />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, teléfono o email..."
                    value={tmpContacto}
                    onChange={e => setTmpContacto(e.target.value)}
                  />
                </div>
                {tmpContacto.trim() && (() => {
                  const q = tmpContacto.toLowerCase()
                  const matches = tasks
                    .filter(t => !t.parent_id && (t.contacto_nombre || t.contacto_apellido))
                    .filter(t => {
                      const name = `${t.contacto_nombre || ''} ${t.contacto_apellido || ''}`.toLowerCase()
                      const phone = (t.contacto_telefono || '').toLowerCase()
                      const email = (t.contacto_email || '').toLowerCase()
                      return name.includes(q) || phone.includes(q) || email.includes(q)
                    })
                  // Deduplicate by contacto_nombre + contacto_apellido
                  const seen = new Set<string>()
                  const unique = matches.filter(t => {
                    const key = `${t.contacto_nombre || ''}|${t.contacto_apellido || ''}`
                    if (seen.has(key)) return false
                    seen.add(key)
                    return true
                  })
                  if (unique.length === 0) return null
                  return (
                    <div style={{
                      marginTop: 8, maxHeight: 140, overflowY: 'auto',
                      border: '1px solid var(--border)', borderRadius: 8,
                      background: 'var(--bg-card)',
                    }}>
                      {unique.map((t, i) => (
                        <div
                          key={i}
                          style={{
                            padding: '8px 12px', cursor: 'pointer',
                            borderBottom: i < unique.length - 1 ? '1px solid var(--border)' : 'none',
                            display: 'flex', flexDirection: 'column', gap: 2,
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          onClick={() => setTmpContacto(`${t.contacto_nombre || ''} ${t.contacto_apellido || ''}`.trim())}
                        >
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.85rem' }}>
                            {t.contacto_nombre} {t.contacto_apellido || ''}
                          </span>
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
                            {t.contacto_telefono && <><IconPhone size={10} /> {t.contacto_telefono}</>}
                            {t.contacto_telefono && t.contacto_email && ' · '}
                            {t.contacto_email && <><IconMail size={10} /> {t.contacto_email}</>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </div>

            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn btn-secondary" onClick={clearFilters}>Limpiar filtros</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setShowFilterModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={applyFilters}>Aplicar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Simple Create Modal ───
function BacklogCreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const api = useApi()
  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    prioridad: 'media',
    notas: '',
    fecha_limite: '',
  })
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    if (!form.titulo.trim()) return
    setSaving(true)
    try {
      await api.post('/api/backlog', {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim() || null,
        prioridad: form.prioridad,
        notas: form.notas.trim() || null,
        fecha_limite: form.fecha_limite || null,
      })
      showToast('Tarea creada', 'success')
      onSaved()
    } catch {
      showToast('Error al crear tarea', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3>Nueva Tarea de Backlog</h3>
          <button className="btn btn-icon btn-secondary" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Título *</label>
            <input
              className="form-input"
              value={form.titulo}
              onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
              placeholder="Ej: Revisar propuesta de diseño web"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-textarea"
              value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              rows={3}
              placeholder="Detalles de la tarea..."
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Prioridad</label>
              <select className="form-select" value={form.prioridad} onChange={e => setForm(f => ({ ...f, prioridad: e.target.value }))}>
                <option value="alta">● Alta</option>
                <option value="media">● Media</option>
                <option value="baja">● Baja</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Fecha límite</label>
              <input
                type="date"
                className="form-input"
                value={form.fecha_limite}
                onChange={e => setForm(f => ({ ...f, fecha_limite: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea
              className="form-textarea"
              value={form.notas}
              onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
              rows={2}
              placeholder="Notas internas..."
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.titulo.trim()}>
            {saving ? 'Guardando...' : 'Crear Tarea'}
          </button>
        </div>
      </div>
    </div>
  )
}
